import React, {
  ReactNode,
  useContext,
  useEffect,
  useCallback,
  useMemo,
  useState,
  useRef,
} from 'react';
import {
  capitalize,
  useMediaQuery,
  useTheme,
  ListItemIcon,
  MenuItem,
  Typography,
  ListItem,
  ListItemText,
  Chip,
  Stack,
} from '@mui/material';
import {
  Property,
  PropertyListItem,
  PanelRootContext,
  PropertyGroup,
  EntityTypes,
  TraxeroActionResponse,
  EntityPanelActionHandler,
  FormMode,
  Status,
  DisplayMode,
  Tenant,
} from 'types';
import { getShortFormattedAddress, logError } from 'utils';
import { useTranslation } from 'react-i18next';
import {
  ActionFunction,
  Outlet,
  useFetcher,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router';

import { AppContext } from 'context/appContext';
import { ROUTES, routeWithParams } from 'router';
import { TOAST_TYPE, ToastContext } from 'context/toastContext';
import MobilePanelList from 'components/lists/MobilePanelList';
import AuthService from 'utils/AuthService';
import {
  createProperty,
  putPropertyGroupProperties,
  updateProperty,
} from 'api';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  MRT_RowVirtualizer,
  MRT_SortingState,
  MRT_Row,
} from 'material-react-table';
import { Add, Delete, History, Warning } from '@mui/icons-material';
import EntityLookup from 'components/autocompletes/EntityLookup';
import { useDefaultTableOptions } from 'hooks/useDefaultTableOptions';
import MRTableToolbar from 'components/tables/PanelTableToolbar';
import { ListItemMenuButton } from 'components/buttons/ListItemMenuButton';
import { useFetcherKey } from 'hooks/useFetcherKey';
import FormHeaderBar from 'components/layout/FormHeaderBar';

export interface PropertyPanelContext {
  properties: Property[] | PropertyListItem[];
  formMode: FormMode;
  displayMode: DisplayMode;
  onBack: () => void;
  fetcherKey: string;
  entity: PropertyGroup | Tenant;
}

export interface PropertyPanelRouteParams {
  params: {
    propertyID?: string;
    tenantID?: string;
    groupID?: string;
  };
}

export interface PropertiesLoaderData {
  _properties: Promise<Property[] | PropertyListItem[]>;
}

const PropertyPanel = (): ReactNode => {
  const { t } = useTranslation();
  const { state } = useContext(AppContext);
  const displayToast = useContext(ToastContext);
  const { breakpoints } = useTheme();
  const mobile = useMediaQuery(breakpoints.down('lg'));
  const { fetcherKey } = useFetcherKey();
  const fetcher = useFetcher({ key: fetcherKey });
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { entity, formMode, displayMode, setFormMode, properties } =
    useOutletContext<PanelRootContext<PropertyGroup | Tenant>>();
  const { tenantID, groupID } =
    useParams() as PropertyPanelRouteParams['params'];

  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const rowVirtualizerInstanceRef = useRef<MRT_RowVirtualizer>(null);

  useEffect(() => {
    //scroll to the top of the table when the sorting changes
    try {
      rowVirtualizerInstanceRef.current?.scrollToIndex?.(0);
    } catch (error) {
      logError(error as Error);
    }
  }, [sorting]);

  // action response handling
  const getSuccessMessage = useCallback(
    (res: TraxeroActionResponse<EntityPanelActionHandler>): string => {
      switch (res.handler) {
        case EntityPanelActionHandler.UPDATE:
          return t('update_success', {
            name: `${t('property.label', { ns: 'entities' })} ${entity?.name}`,
            ns: 'toasts',
          });
        case EntityPanelActionHandler.GROUP_CREATE:
        case EntityPanelActionHandler.TENANT_CREATE:
          return t('create_success', {
            name: `${t('property.label', { ns: 'entities' })}`,
            ns: 'toasts',
          });
        case EntityPanelActionHandler.GROUP_UPSERT:
          return t('update_success', {
            name: `${t('property_group.label', { ns: 'entities' })}`,
            ns: 'toasts',
          });
        case EntityPanelActionHandler.DELETE:
          return t('delete_success', {
            name: `${t('property.label', { ns: 'entities' })} ${
              res.body?.name || ''
            }`,
            ns: 'toasts',
          });
        default:
          return '';
      }
    },
    [],
  );
  const getErrorMessage = useCallback(
    (res: TraxeroActionResponse<EntityPanelActionHandler>): string => {
      const { handler, status, statusText } = res;
      switch (handler) {
        case EntityPanelActionHandler.UPDATE:
          return t('update_error', {
            name: `${t('property.label', { ns: 'entities' })}`,
            error: `${status} - ${statusText}`,
            ns: 'toasts',
          });
        case EntityPanelActionHandler.TENANT_CREATE:
          return t('create_error', {
            name: `${t('property.label', { ns: 'entities' })}`,
            error: `${status} - ${statusText}`,
            ns: 'toasts',
          });
        case EntityPanelActionHandler.GROUP_UPSERT:
          return t('update_error', {
            name: `${t('property_group.label', { ns: 'entities' })} ${t(
              'property.label_plural',
              { ns: 'entities' },
            )}`,
            error: `${status} - ${statusText}`,
            ns: 'toasts',
          });
        case EntityPanelActionHandler.DELETE:
          return t('delete_error', {
            name: `${t('property.label', { ns: 'entities' })}`,
            error: `${status} - ${statusText}`,
            ns: 'toasts',
          });
        default:
          return t('unknown_error', { ns: 'toasts' });
      }
    },
    [],
  );
  const response =
    fetcher.data as TraxeroActionResponse<EntityPanelActionHandler> | null;
  useEffect(() => {
    if (response) {
      if (response.status < 400) {
        displayToast({
          type: TOAST_TYPE.SUCCESS,
          message: getSuccessMessage(response),
        });
        if (response.handler !== EntityPanelActionHandler.DELETE) {
          handleCancelAction();
        }
      } else {
        displayToast({
          type: TOAST_TYPE.ERROR,
          message: getErrorMessage(response),
        });
      }
    }
  }, [fetcher]);

  const canAddRemove: boolean = useMemo(() => {
    if (AuthService.IsSuperAdmin() && displayMode !== 'tenant') {
      return true;
    }
    if (displayMode === 'property') {
      return state.entityPermissions.property.update;
    }
    if (displayMode === 'group') {
      return state.entityPermissions.property_group.update;
    }
    // cannot add/remove properties from tenant
    return false;
  }, [displayMode]);

  const handleRemoveProperty = (id: number) => {
    const updatedProperties = properties?.filter((p) => p.id !== id) || [];
    fetcher.submit(
      {
        handler: EntityPanelActionHandler.GROUP_UPSERT,
        body: JSON.stringify(updatedProperties),
      },
      {
        method: 'PUT',
        action: pathname,
        encType: 'application/json',
      },
    );
  };
  const handleCreateClick = () => {
    setFormMode('create');
    navigate(routeWithParams(ROUTES.PROPERTY_CREATE, [tenantID || 0]));
  };

  const handleItemClick = (id: number) => {
    navigate(routeWithParams(ROUTES.PROPERTY, [tenantID || 0, id]));
  };
  const handleViewRevisions = (id: number) => {
    navigate(
      routeWithParams(ROUTES.REVISIONS, [
        state.tenant.id,
        EntityTypes.Property,
        id.toString(),
      ]),
    );
  };
  const handleAddProperty = (p: Property | null) => {
    if (p) {
      if (properties?.some((prop) => prop.id === p.id)) {
        return;
      }

      const updatedProperties = [...(entity as PropertyGroup).properties, p];
      fetcher.submit(
        {
          handler: EntityPanelActionHandler.GROUP_UPSERT,
          body: JSON.stringify(updatedProperties),
        },
        {
          method: 'PUT',
          encType: 'application/json',
        },
      );
    }
  };

  const handleCancelAction = useCallback(() => {
    setFormMode('none');
    const route = getPanelRouteForDisplayMode(
      displayMode,
      state.tenant.id,
      groupID,
    );
    navigate(route, { replace: true });
  }, [displayMode]);

  const handleSoftDelete = (property: Property | PropertyListItem): void => {
    const updatedProperty = { ...property, status: Status.Disabled };
    fetcher.submit(
      {
        handler: EntityPanelActionHandler.DELETE,
        body: JSON.stringify(updatedProperty),
      },
      {
        method: 'PUT',
        action: pathname,
        encType: 'application/json',
      },
    );
  };

  const columns = useMemo<MRT_ColumnDef<Property | PropertyListItem>[]>(
    () => [
      {
        header: t('property.id', { ns: 'entities' }),
        accessorKey: 'id',
      },
      {
        header: t('property.externalID', { ns: 'entities' }),
        accessorKey: 'externalID',
      },
      {
        header: t('property.name', { ns: 'entities' }),
        accessorKey: 'name',
        size: 350,
        Cell: ({
          row,
        }: {
          row: MRT_Row<Property | PropertyListItem>;
          renderedCellValue: ReactNode;
        }) => {
          const disableTowing =
            'data' in row.original
              ? row.original.data?.disableTowing
              : row.original.disableTowing;
          return (
            <Stack
              flexDirection={'row'}
              alignItems={'center'}
              justifyContent={'space-between'}
              width={350}
              display={'flex'}
            >
              <Typography variant={'body2'} noWrap>
                {row.original.name}
              </Typography>
              {disableTowing && (
                <Chip
                  color={'error'}
                  icon={<Warning />}
                  label={t('donottow.label', { ns: 'entities' })}
                  variant={'outlined'}
                  size={'small'}
                  sx={{
                    fontWeight: 'bold',
                    px: 1,
                    ml: 2,
                  }}
                />
              )}
            </Stack>
          );
        },
      },
      {
        header: t('property.description', { ns: 'entities' }),
        accessorKey: 'description',
        size: 200,
      },
      {
        header: t('property.location.label', { ns: 'entities' }),
        id: 'address',
        accessorFn: (row) => getShortFormattedAddress(row.location),
        size: 300,
      },
      {
        header: t('property.type', { ns: 'entities' }),
        id: 'type',
        accessorFn: (row) => capitalize(row.type),
      },
      {
        header: t('property.status', { ns: 'entities' }),
        accessorKey: 'status',
        filterVariant: 'select',
        filterSelectOptions: [
          {
            label: t('status.active', { ns: 'enums' }),
            value: Status.Active,
          },
          {
            label: t('status.disabled', { ns: 'enums' }),
            value: Status.Disabled,
          },
        ],
        id: 'status',
        Cell: ({ row }: { row: MRT_Row<Property | PropertyListItem> }) => {
          return capitalize(row.original.status);
        },
      },
    ],
    [],
  );
  const tableDefaults = useDefaultTableOptions<Property | PropertyListItem>();
  const table = useMaterialReactTable({
    ...tableDefaults,
    data: properties || [],
    columns,
    enableRowVirtualization: true,
    enableRowSelection: false,
    enableColumnOrdering: false,
    initialState: {
      showColumnFilters: false,
      showGlobalFilter: true,
      density: 'comfortable',
      columnVisibility: {
        id: false,
        externalID: false,
        name: true,
        description: true,
        address: true,
        type: true,
        status: false,
      },
      columnPinning: {
        right: ['mrt-row-actions'],
      },
    },
    state: {
      sorting,
      showLoadingOverlay: false,
    },
    onSortingChange: setSorting,
    getRowId: (row) => `${row.id}`,
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor: 'white',
        cursor: 'pointer',
      },
      onClick: () => handleItemClick(row.original.id),
    }),
    renderTopToolbar: ({ table }) => (
      <MRTableToolbar
        table={table}
        hidden={formMode !== 'none'}
        isSubmitting={fetcher.state === 'submitting'}
        optionsDisabled={!state.entityPermissions.property.create}
        menuOptions={[
          {
            onClick: () => handleCreateClick(),
            content: t('buttons.create', {
              name: t('property.label', { ns: 'entities' }),
            }),
            disabled: !state.entityPermissions.property.create,
            icon: <Add />,
          },
        ]}
      >
        <Stack
          direction={'row'}
          flexGrow={1}
          justifyContent={'flex-end'}
          alignItems={'center'}
        >
          {displayMode !== 'tenant' && (
            <EntityLookup
              entityType={EntityTypes.Property}
              onSelect={handleAddProperty}
              renderOption={(option: Property) => {
                return (
                  <React.Fragment key={option.id}>
                    <Typography variant={'body2'} noWrap fontWeight={'bold'}>
                      {option.name}
                    </Typography>
                    <Typography variant={'body2'} noWrap>
                      {option.description}
                    </Typography>
                  </React.Fragment>
                );
              }}
              size='small'
              sx={{ maxWidth: '70%' }}
            />
          )}
        </Stack>
      </MRTableToolbar>
    ),
    renderRowActionMenuItems: ({ closeMenu, row }) => {
      const options = [
        <MenuItem
          key={1}
          divider
          onClick={() => {
            handleViewRevisions(row.original.id);
            closeMenu();
          }}
          sx={{ m: 0, backgroundColor: 'white' }}
        >
          <ListItemIcon>
            <History />
          </ListItemIcon>
          {t('buttons.view', { name: t('revision.label_plural') })}
        </MenuItem>,
      ];
      if (canAddRemove && displayMode !== 'tenant') {
        options.push(
          <MenuItem
            key={2}
            onClick={() => handleRemoveProperty(row.original.id)}
          >
            <ListItemIcon>
              <Delete />
            </ListItemIcon>
            {t('buttons.remove', {
              name: t('property.label', { ns: 'entities' }),
            })}
          </MenuItem>,
        );
      }
      if (AuthService.IsSuperAdmin()) {
        options.push(
          <MenuItem
            key={3}
            onClick={() => {
              handleSoftDelete(row.original);
              closeMenu();
            }}
            sx={{
              m: 0,
              backgroundColor: 'warning.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'warning.dark',
              },
            }}
          >
            <ListItemIcon>
              <Warning sx={{ color: 'white' }} />
            </ListItemIcon>
            {t('buttons.delete', {
              name: t('property.label', { ns: 'entities' }),
            })}
          </MenuItem>,
        );
      }
      return options;
    },
  });

  if (formMode !== 'none') {
    return (
      <React.Fragment>
        <FormHeaderBar />
        <Outlet
          context={
            {
              properties: properties || [],
              formMode,
              onBack: handleCancelAction,
              displayMode,
              fetcherKey,
              entity,
            } satisfies PropertyPanelContext
          }
        />
      </React.Fragment>
    );
  }

  if (mobile) {
    const itemHeight = 70;
    const renderItem = useCallback(
      (property: Property | PropertyListItem, index: number): ReactNode => (
        <ListItem
          key={`property-${property.id}-${index}`}
          onClick={() =>
            navigate(
              routeWithParams(ROUTES.PROPERTY, [`${tenantID}`, property.id]),
            )
          }
          divider
          sx={{ height: itemHeight, backgroundColor: 'white' }}
          secondaryAction={
            <ListItemMenuButton
              menuOptions={[
                {
                  onClick: () => handleRemoveProperty(property.id),
                  content: t('buttons.remove', { name: t('property.label', { ns: 'entities' }) }),
                  disabled: !canAddRemove,
                },
              ]}
            />
          }
        >
          <ListItemText
            primary={
              <Typography variant={'subtitle1'} noWrap>
                {property.name}
              </Typography>
            }
            secondary={
              <Typography variant={'subtitle2'} noWrap>
                {getShortFormattedAddress(property.location)}
              </Typography>
            }
          />
        </ListItem>
      ),
      [properties || []],
    );

    return (
      <MobilePanelList
        data={properties || []}
        columns={columns.map((c) => ({
          name: c.header,
          searchKey: c.accessorKey,
        }))}
        renderItem={renderItem}
        itemHeight={70}
        title={t('property.label_plural', { ns: 'entities' })}
        withSearchBar
        menuOptions={[
          {
            onClick: () => handleCreateClick(),
            content: t('buttons.create', { name: t('property.label', { ns: 'entities' }) }),
            disabled: !canAddRemove,
            icon: <Add />,
          },
        ]}
      />
    );
  }

  return <MaterialReactTable table={table} />;
};

export const getPanelRouteForDisplayMode = (
  displayMode: DisplayMode,
  tenantID: number | string,
  groupID: number | string = 0,
) => {
  switch (displayMode) {
    case 'tenant':
      return routeWithParams(ROUTES.PROPERTIES, [tenantID]);
    case 'group':
      return routeWithParams(ROUTES.PROPERTY_GROUP_PROPERTIES, [
        tenantID,
        groupID,
      ]);
    default:
      return '';
  }
};

export const propertyPanelAction: ActionFunction = async ({
  params,
  request,
}): Promise<TraxeroActionResponse<EntityPanelActionHandler>> => {
  const { body, handler } = await request.json();
  const { tenantID, groupID } = params as PropertyPanelRouteParams['params'];
  let res: Response;
  let propertyData: Property | null = null;

  try {
    switch (handler) {
      case EntityPanelActionHandler.TENANT_CREATE: {
        res = await createProperty({ tenantID, body });
        break;
      }
      case EntityPanelActionHandler.GROUP_CREATE:
      case EntityPanelActionHandler.GROUP_UPSERT: {
        res = await putPropertyGroupProperties({
          tenantID,
          groupID,
          body,
        });
        break;
      }
      case EntityPanelActionHandler.DELETE: {
        const property = JSON.parse(body) as Property;
        propertyData = property;
        res = await updateProperty({
          tenantID,
          propertyID: property.id,
          body,
        });
        break;
      }
      default: {
        res = new Response('Unknown handler', {
          status: 404,
          statusText: 'Unknown Handler',
        });
      }
    }
  } catch (error) {
    res = new Response('Internal Server Error', {
      status: 500,
      statusText: 'Internal Server Error',
    });
  }
  return {
    handler,
    status: res.status,
    statusText: res.statusText,
    body: propertyData,
  };
};

export default PropertyPanel;
